import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Plug, Save, Info, FileCode2,
} from 'lucide-react';
import { toast } from 'sonner';
import { HealthCheckPanel } from '../../components/accountsiq/HealthCheckPanel';
import { TestInvoiceCard } from '../../components/accountsiq/TestInvoiceCard';
import { errMsg } from '../../components/accountsiq/aiqMeta';

/**
 * Admin page for configuring the AccountsIQ SOAP API integration.
 * Supports both authentication versions (1.1 legacy / 2.0 OAuth).
 * Secrets are stored AES-GCM encrypted and rendered as •••••••• in the UI.
 */
export default function AccountsIQSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [introspecting, setIntrospecting] = useState(false);
  const [wsdlReport, setWsdlReport] = useState(null);
  const [settings, setSettings] = useState({
    enabled: false,
    auth_version: '2.0',
    wsdl_url: 'https://eu1.accountsiq.com/system/dashboard/integration/integration_2_0.asmx?wsdl',
    region: 'eu1',
    company_code: '',
    entity_id: '',
    client_id: '',
    client_secret: '',
    partner_key: '',
    user_key: '',
    user_id: '',
    post_as_draft: true,
    max_retries: 3,
    nightly_sync_enabled: false,
    rest_enabled: true,
    nightly_sync_time: '03:00',
    escalation_email: '',
    last_test_at: null,
    last_test_result: null,
    last_nightly_sync_at: null,
    last_nightly_sync_stats: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/accountsiq/settings');
      setSettings(s => ({ ...s, ...data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const onRegionChange = (region) => {
    update('region', region);
    // Auto-adjust the WSDL host to match the new region (operator can still edit it).
    update('wsdl_url',
      `https://${region}.accountsiq.com/system/dashboard/integration/integration_${settings.auth_version === '1.1' ? '1_1' : '2_0'}.asmx?wsdl`);
  };

  const onAuthVersionChange = (v) => {
    update('auth_version', v);
    // Re-derive WSDL URL for the new auth version.
    update('wsdl_url',
      `https://${settings.region || 'eu1'}.accountsiq.com/system/dashboard/integration/integration_${v === '1.1' ? '1_1' : '2_0'}.asmx?wsdl`);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip masked placeholder so the backend keeps the existing encrypted value.
      const payload = { ...settings };
      ['client_secret', 'partner_key', 'user_key'].forEach(k => {
        if (payload[k] === '••••••••') payload[k] = '';
      });
      await api.put('/accountsiq/settings', payload);
      toast.success('AccountsIQ settings saved');
      await load();
    } catch (e) {
      const bad = Array.isArray(e.response?.data?.detail) && e.response.data.detail.some((d) => d.loc?.includes('escalation_email'));
      toast.error(bad ? 'Alert email address is not valid' : errMsg(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const introspectWsdl = async () => {
    setIntrospecting(true);
    setWsdlReport(null);
    try {
      const { data } = await api.get('/accountsiq/wsdl-operations');
      setWsdlReport(data);
      if ((data.missing_required || []).length) {
        toast.warning(`WSDL is missing ${data.missing_required.length} required method(s)`);
      } else {
        toast.success(`Found ${data.operation_count} SOAP operations`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not introspect WSDL';
      setWsdlReport({ error: msg });
      toast.error(msg);
    } finally {
      setIntrospecting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading AccountsIQ settings…</div>;
  }

  const isV2 = settings.auth_version === '2.0';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="accountsiq-settings-page">
      <div className="flex items-center gap-2 justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Data Management
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/settings/accountsiq-sync')} data-testid="aiq-settings-open-sync">
          Open AccountsIQ Sync
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Plug className="w-6 h-6 text-indigo-600" />
          AccountsIQ Integration
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Push dispatched orders as draft invoices into AccountsIQ. Supports both auth versions; switch any time.
        </p>
      </div>

      {/* Help */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="pt-5 pb-4 text-sm flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-blue-900 space-y-1">
            <p>Fill in the connection details and <strong>Save</strong>, then press <strong>Run full check</strong> to confirm sign-in, customers, products, prices, tax codes, currencies and draft-invoice access all work.</p>
            <p>Invoices are always created as <strong>drafts</strong>, after the office reviews the invoice preview, so your accountant posts them in AccountsIQ.</p>
          </div>
        </CardContent>
      </Card>

      {/* Main form */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base">API Configuration</CardTitle>
          <CardDescription>WSDL URL is editable so you can swap sandbox/production without redeploying.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enabled */}
          <div className="flex items-center gap-3">
            <Switch
              id="aiq-enabled"
              checked={!!settings.enabled}
              onCheckedChange={(v) => update('enabled', v)}
              data-testid="switch-aiq-enabled"
            />
            <Label htmlFor="aiq-enabled" className="text-sm">
              Enabled: when off, nothing is read from or sent to AccountsIQ.
            </Label>
          </div>

          {/* Region + auth-version + post-as-draft */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Region</Label>
              <Select value={settings.region || 'eu1'} onValueChange={onRegionChange}>
                <SelectTrigger data-testid="select-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="uk1">UK (uk1)</SelectItem>
                  <SelectItem value="eu1">Europe 1 (eu1)</SelectItem>
                  <SelectItem value="eu2">Europe 2 (eu2)</SelectItem>
                  <SelectItem value="us1">US (us1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Authentication</Label>
              <Select value={settings.auth_version || '2.0'} onValueChange={onAuthVersionChange}>
                <SelectTrigger data-testid="select-auth-version"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2.0">v2.0 — OAuth (recommended)</SelectItem>
                  <SelectItem value="1.1">v1.1 — Session Token (legacy)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Post as Draft</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch
                  id="aiq-draft"
                  checked={!!settings.post_as_draft}
                  onCheckedChange={(v) => update('post_as_draft', v)}
                  data-testid="switch-post-as-draft"
                />
                <Label htmlFor="aiq-draft" className="text-sm text-muted-foreground">
                  Accountant reviews before posting
                </Label>
              </div>
            </div>
          </div>

          {/* WSDL URL */}
          <div>
            <Label className="text-xs">WSDL URL</Label>
            <Input
              value={settings.wsdl_url || ''}
              onChange={(e) => update('wsdl_url', e.target.value)}
              placeholder="https://eu1.accountsiq.com/system/dashboard/integration/integration_2_0.asmx?wsdl"
              className="font-mono text-xs"
              data-testid="input-wsdl-url"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Defaults are derived from Region + Auth Version. Override here for sandbox or custom endpoints.
            </p>
          </div>

          {/* Common identifiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Company Code</Label>
              <Input
                value={settings.company_code || ''}
                onChange={(e) => update('company_code', e.target.value)}
                placeholder="CASHELBLUE"
                data-testid="input-company-code"
              />
            </div>
            <div>
              <Label className="text-xs">Entity ID</Label>
              <Input
                value={settings.entity_id || ''}
                onChange={(e) => update('entity_id', e.target.value)}
                placeholder="Same as Company Code in many tenants"
                data-testid="input-entity-id"
              />
            </div>
          </div>

          {/* Version-specific credentials */}
          {isV2 ? (
            <div className="border rounded-md p-3 bg-zinc-50/40 space-y-3" data-testid="creds-v2-block">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-600">OAuth 2.0 credentials</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client ID</Label>
                  <Input
                    value={settings.client_id || ''}
                    onChange={(e) => update('client_id', e.target.value)}
                    autoComplete="off"
                    data-testid="input-client-id"
                  />
                </div>
                <div>
                  <Label className="text-xs">Client Secret</Label>
                  <Input
                    type="password"
                    value={settings.client_secret || ''}
                    onChange={(e) => update('client_secret', e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="input-client-secret"
                  />
                </div>
              </div>
              <div className="flex items-start gap-3 pt-1">
                <Switch
                  id="aiq-rest"
                  checked={settings.rest_enabled !== false}
                  onCheckedChange={(v) => update('rest_enabled', v)}
                  data-testid="switch-rest-enabled"
                />
                <div>
                  <Label htmlFor="aiq-rest" className="text-sm">Use the REST API for tax codes &amp; currencies</Label>
                  <p className="text-xs text-muted-foreground">
                    AccountsIQ&apos;s newer REST connection (beta). When off, or if AccountsIQ rejects it, tax codes and currencies are read through the standard connection instead.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="border rounded-md p-3 bg-zinc-50/40 space-y-3" data-testid="creds-v1-block">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-600">v1.1 session-token credentials</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Partner Key</Label>
                  <Input
                    type="password"
                    value={settings.partner_key || ''}
                    onChange={(e) => update('partner_key', e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="input-partner-key"
                  />
                </div>
                <div>
                  <Label className="text-xs">User Key</Label>
                  <Input
                    type="password"
                    value={settings.user_key || ''}
                    onChange={(e) => update('user_key', e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    data-testid="input-user-key"
                  />
                </div>
                <div>
                  <Label className="text-xs">User ID</Label>
                  <Input
                    value={settings.user_id || ''}
                    onChange={(e) => update('user_id', e.target.value)}
                    data-testid="input-user-id"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={save} disabled={saving} data-testid="btn-save-aiq">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Settings
            </Button>
            <p className="text-xs text-muted-foreground">Save first: the connection check below uses the saved settings.</p>
          </div>
        </CardContent>
      </Card>

      <HealthCheckPanel
        health={settings.last_health_check}
        alert={settings}
        onRan={(h) => setSettings((s) => ({ ...s, last_health_check: h }))}
      />
      <TestInvoiceCard
        sample={settings.last_health_check?.sample}
        last={settings.last_test_invoice}
        onCreated={(rec) => setSettings((s) => ({ ...s, last_test_invoice: rec }))}
      />

      {/* Phase 2 — Automation */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base">Automation</CardTitle>
          <CardDescription>Nightly customer + product sync and retry-worker escalation.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="aiq-nightly"
              checked={!!settings.nightly_sync_enabled}
              onCheckedChange={(v) => update('nightly_sync_enabled', v)}
              data-testid="switch-nightly-sync"
            />
            <Label htmlFor="aiq-nightly" className="text-sm">
              Enable nightly customer + product pull from AccountsIQ
            </Label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nightly check time (UTC)</Label>
              <Input
                value={settings.nightly_sync_time || '03:00'}
                onChange={(e) => update('nightly_sync_time', e.target.value)}
                placeholder="03:00"
                className="w-32"
                data-testid="input-nightly-time"
              />
              <p className="text-xs text-muted-foreground mt-1">24-hour format. The connection check runs every night at this time; the pull above runs too when switched on.</p>
            </div>
            <div>
              <Label className="text-xs">Alert email</Label>
              <Input
                type="email"
                value={settings.escalation_email || ''}
                onChange={(e) => update('escalation_email', e.target.value)}
                placeholder="accounts@yourcompany.com"
                data-testid="input-escalation-email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Emailed when the nightly check or refresh fails. Uses the email (SMTP) settings under Settings.
              </p>
            </div>
          </div>
          {settings.last_nightly_sync_at && (
            <div className="text-xs text-muted-foreground border-t pt-3">
              <p>Last nightly sync: {new Date(settings.last_nightly_sync_at).toLocaleString('en-IE')}</p>
              {settings.last_nightly_sync_stats && (
                <p className="font-mono mt-1">
                  customers: +{settings.last_nightly_sync_stats.customers_added} added,{' '}
                  {settings.last_nightly_sync_stats.customers_conflicts} conflicts
                  · products: +{settings.last_nightly_sync_stats.products_added} added,{' '}
                  {settings.last_nightly_sync_stats.products_conflicts} conflicts
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* WSDL diagnostic */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base flex items-center gap-2">
            <FileCode2 className="w-5 h-5 text-slate-600" /> WSDL Method Diagnostic
          </CardTitle>
          <CardDescription>
            Verify your AccountsIQ tenant exposes the SOAP methods this app calls. Useful when the
            nightly pull writes silent errors (e.g. some tenants expose <code>GetCustomerList</code> instead of <code>GetCustomers</code>).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={introspectWsdl} disabled={introspecting} data-testid="btn-introspect-wsdl">
            {introspecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileCode2 className="w-4 h-4 mr-1" />}
            Check WSDL Methods
          </Button>
          {wsdlReport && !wsdlReport.error && (
            <div className="text-sm space-y-2" data-testid="wsdl-report">
              <p className="text-muted-foreground">
                Found <strong>{wsdlReport.operation_count}</strong> SOAP operation(s) on{' '}
                <span className="font-mono text-xs">{wsdlReport.wsdl_url}</span>
              </p>
              {wsdlReport.missing_required?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-red-800 text-sm">
                  <p className="font-semibold flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Missing required methods:</p>
                  <p className="font-mono text-xs mt-1">{wsdlReport.missing_required.join(', ')}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 mt-2">
                {Object.entries(wsdlReport.expected_operations || {}).map(([name, present]) => (
                  <div key={name} className="flex items-center gap-2 text-xs font-mono">
                    {present
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      : <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    <span className={present ? 'text-emerald-900' : 'text-amber-800'}>{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {wsdlReport?.error && (
            <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-red-800 text-sm">
              {wsdlReport.error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
